import { Injectable, Injector, ComponentRef, TemplateRef, ElementRef } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef, OverlayPositionBuilder, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';

import { PopoverRef } from './popover-ref';
import { POPOVER_CONTENT_DATA } from './popover.tokens';
import { PopoverContainer } from './popover-container.component';
import { Subscription, fromEvent } from 'rxjs';
import { skip, filter, take } from 'rxjs/operators';
import { PopoverContent } from './popover-content';

const positions = {
  bottom: {
    originY: 'bottom',
    overlayY: 'top',
    offsetY: 4,
  },
  top: {
    originY: 'top',
    overlayY: 'bottom',
    offsetY: -4,
  }
};

const alignments = {
  left: {
    originX: 'start',
    overlayX: 'start',
  },
  right: {
    originX: 'end',
    overlayX: 'end',
  },
  center: {
    originX: 'center',
    overlayX: 'center',
  }
}

export interface PopoverConfig {
  content?: PopoverContent;
  position: 'top' | 'bottom';
  alignment: 'left' | 'right' | 'center';
}

const DEFAULT_CONFIG: PopoverConfig = {
  content: null,
  position: 'bottom',
  alignment: 'left',
};

@Injectable()
export class PopoverService {
  constructor(
    private injector: Injector,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
  ) { }

  open(config: PopoverConfig = DEFAULT_CONFIG, target: ElementRef | HTMLElement) {
    // Override default configuration
    const popoverConfig = { ...DEFAULT_CONFIG, ...config };

    // Returns an OverlayRef which is a PortalHost
    const overlayRef = this.createOverlay(popoverConfig, target);

    // Instantiate remote control
    const popoverRef = new PopoverRef(overlayRef);

    const overlayComponent = this.attachPopoverContainer(overlayRef, popoverConfig, popoverRef);

    popoverRef.componentInstance = overlayComponent;

    overlayRef.backdropClick().subscribe(_ => popoverRef.close());

    return popoverRef;
  }

  private createOverlay(config: PopoverConfig, target: ElementRef | HTMLElement) {
    const overlayConfig = this.getOverlayConfig(config, target);
    return this.overlay.create(overlayConfig);
  }

  private attachPopoverContainer(overlayRef: OverlayRef, config: PopoverConfig, popoverRef: PopoverRef) {
    const injector = this.createInjector(config, popoverRef);

    const containerPortal = new ComponentPortal(PopoverContainer, null, injector);
    const containerRef: ComponentRef<PopoverContainer> = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }

  private createInjector(config: PopoverConfig, popoverRef: PopoverRef): PortalInjector {
    const injectionTokens = new WeakMap();

    injectionTokens.set(PopoverRef, popoverRef);
    injectionTokens.set(POPOVER_CONTENT_DATA, config.content);

    return new PortalInjector(this.injector, injectionTokens);
  }

  private getOverlayConfig(config: PopoverConfig, target: ElementRef | HTMLElement): OverlayConfig {
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(target)
      .withPositions([{ ...positions[config.position], ...alignments[config.alignment] } as ConnectedPosition]);

    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy
    });

    return overlayConfig;
  }
}
